<template>
  <v-form ref="form" v-model="valid">
    <h3 class="title mb-4">Tenancy Management Fee Override</h3>

    <v-row>
      <v-col cols="12">
        <v-alert type="info" outlined class="mb-4">
          <div class="font-weight-medium mb-2">About Tenancy Management Fees</div>
          <p class="mb-0">
            A tenancy management fee overrides the landlord's standard management fee rate
            for this specific tenancy. Use this when you want to apply a different fee
            rate for this particular tenancy agreement.
          </p>
        </v-alert>

        <v-alert
          v-if="showLandlordFeeWarning"
          type="warning"
          outlined
          class="mb-4"
        >
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          <strong>Note:</strong> The tenancy management fee ({{ propertyFeeData.managementFeePercent }}%)
          will override the landlord's management fee ({{ landlordData.newLandlord.managementFeePercent }}%)
          for this tenancy only.
        </v-alert>
      </v-col>
    </v-row>

    <!-- Toggle Switch -->
    <v-row>
      <v-col cols="12">
        <v-switch
          v-model="propertyFeeData.overrideEnabled"
          label="Override landlord's management fee for this tenancy"
          color="primary"
          hide-details
          class="mt-0"
        ></v-switch>
      </v-col>
    </v-row>

    <!-- Conditional Fields (shown when toggle is enabled) -->
    <v-expand-transition>
      <div v-if="propertyFeeData.overrideEnabled">
        <v-divider class="my-4" />

        <v-row>
          <v-col cols="12">
            <p class="text-caption mb-3">
              Both percentage and effective date must be provided together
            </p>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="propertyFeeData.managementFeePercent"
              label="Tenancy Management Fee (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              suffix="%"
              outlined
              dense
              :rules="[rules.managementFeePercent]"
              hint="Management fee for this tenancy (0-100%)"
              persistent-hint
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="propertyFeeData.dateManagementFee"
                  label="Applies From"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  :rules="[rules.dateManagementFee]"
                  hint="Date from which this fee applies"
                  persistent-hint
                  clearable
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="propertyFeeData.dateManagementFee"
                :min="todayDate"
                @input="dateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>

    <!-- Navigation buttons -->
    <v-row class="mt-6">
      <v-col cols="6">
        <v-btn text @click="$emit('back')">
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-col>
      <v-col cols="6" class="text-right">
        <v-btn
          color="primary"
          :disabled="!canProceed"
          @click="proceed"
        >
          Next
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
export default {
  name: 'PropertyManagementFeeStep',

  props: {
    value: {
      type: Object,
      required: true
    },
    landlordData: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      valid: false,
      dateMenu: false,
      propertyFeeData: this.value,
      rules: {
        managementFeePercent: v => {
          // Only validate if override is enabled
          if (!this.propertyFeeData.overrideEnabled) return true

          // If enabled and date has value, percentage is required
          if (this.propertyFeeData.dateManagementFee && !v) {
            return 'Management fee percentage is required when date is provided'
          }

          // If provided, validate range
          if (v !== null && v !== '') {
            if (v < 0 || v > 100) {
              return 'Management fee must be between 0 and 100'
            }
          }

          return true
        },
        dateManagementFee: v => {
          // Only validate if override is enabled
          if (!this.propertyFeeData.overrideEnabled) return true

          // If enabled and percentage has value, date is required
          if ((this.propertyFeeData.managementFeePercent !== null &&
               this.propertyFeeData.managementFeePercent !== '') && !v) {
            return 'Management fee date is required when percentage is provided'
          }

          // If provided, validate that it's today or in the future
          if (v) {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const selectedDate = new Date(v)
            if (selectedDate < today) {
              return 'Management fee date must be today or in the future'
            }
          }

          return true
        }
      }
    }
  },

  computed: {
    todayDate() {
      return new Date().toISOString().substr(0, 10)
    },

    canProceed() {
      // If override is disabled, can always proceed
      if (!this.propertyFeeData.overrideEnabled) return true

      // If override is enabled, must have valid form and both fields filled
      return this.valid &&
             this.propertyFeeData.managementFeePercent !== null &&
             this.propertyFeeData.managementFeePercent !== '' &&
             this.propertyFeeData.dateManagementFee
    },

    showLandlordFeeWarning() {
      // Check if property override is enabled AND landlord has management fee
      return this.propertyFeeData.overrideEnabled &&
             this.landlordData &&
             this.landlordData.newLandlord &&
             this.landlordData.newLandlord.managementFeePercent !== null &&
             this.landlordData.newLandlord.managementFeePercent !== ''
    }
  },

  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.propertyFeeData = newVal
      }
    },
    propertyFeeData: {
      deep: true,
      handler(val) {
        this.$emit('input', val)
      }
    },
    'propertyFeeData.overrideEnabled'(enabled) {
      // Clear fields when toggle is disabled
      if (!enabled) {
        this.propertyFeeData.managementFeePercent = null
        this.propertyFeeData.dateManagementFee = null
      }
    }
  },

  methods: {
    proceed() {
      if (this.canProceed) {
        this.$emit('next')
      }
    }
  }
}
</script>
